import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { openTelegramLink } from '@telegram-apps/sdk-react';
import { useTranslation } from 'react-i18next';
import { login } from '@/api/auth';
import { ApiError, errorCode, setAccessToken } from '@posilka/client/api/client';
import { meKey } from '@posilka/client/api/me';
import { BrandMark } from '@/components/BrandMark';

/** Ilova ochilganda avtomatik kirish (SPEC §8). Muvaffaqiyatsiz bo'lsa — ichki sahifalar ko'rinmaydi. */
export function AuthGate({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [ready, setReady] = useState(false);
  const auth = useQuery({
    queryKey: ['auth'],
    queryFn: login,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
  });

  useEffect(() => {
    if (!auth.data) return;
    setAccessToken(auth.data.accessToken);
    qc.setQueryData(meKey, auth.data.user);
    // Server tili — asosiy manba (foydalanuvchi botda yoki profilda tanlagan). Faqat kirishda bir marta.
    void i18n.changeLanguage(auth.data.user.language).then(() => setReady(true));
  }, [auth.data, qc, i18n]);

  if (auth.isPending || (auth.data && !ready)) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4">
        <BrandMark className="h-16 w-16 animate-pulse rounded-[18px] shadow-[0_8px_40px_-8px_var(--glow)]" />
        <span className="font-display text-lg font-bold tracking-tight">Jo&apos;natmaBor</span>
      </div>
    );
  }

  if (auth.isError) {
    const code = errorCode(auth.error);
    const supportUrl =
      auth.error instanceof ApiError
        ? (auth.error.details as { supportUrl?: string | null } | undefined)?.supportUrl
        : null;
    const message =
      code === 'USER_BLOCKED'
        ? t('auth.blocked')
        : code === 'INIT_DATA_INVALID'
          ? t('auth.invalid')
          : code === 'NETWORK_ERROR' || code === 'TIMEOUT' || code === 'SERVER_UNAVAILABLE'
            ? t(`errors.${code}`)
            : t('auth.failed');
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <span className="grid h-16 w-16 place-items-center rounded-2xl bg-danger/12 text-3xl">
          ⚠️
        </span>
        <p className="text-base">{message}</p>
        {code === 'USER_BLOCKED' && supportUrl && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              if (openTelegramLink.isAvailable()) openTelegramLink(supportUrl);
              else window.open(supportUrl, '_blank');
            }}
          >
            ✉️ {t('auth.contactAdmin')}
          </button>
        )}
        {code !== 'USER_BLOCKED' && (
          <button type="button" className="btn-primary" onClick={() => void auth.refetch()}>
            {t('common.retry')}
          </button>
        )}
      </div>
    );
  }

  return children;
}
